import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';

export const CONSENT_KEY = 'ido_cookie_consent';

export const getCookieConsent = () => {
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveConsent = (status) => {
  const record = { status, at: new Date().toISOString() };
  window.localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  window.dispatchEvent(new CustomEvent('ido-cookie-consent', { detail: record }));
};

const CookieConsent = () => {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (getCookieConsent()) return undefined;
    const timer = setTimeout(() => setVisible(true), 900);
    return () => clearTimeout(timer);
  }, []);

  if (!visible || pathname.startsWith('/admin')) return null;

  const decide = (status) => {
    saveConsent(status);
    setVisible(false);
  };

  return (
    <div
      className="cookie-banner fixed bottom-4 left-4 right-4 sm:left-6 sm:right-auto sm:max-w-md z-[90]"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
    >
      <div className="relative bg-white/95 backdrop-blur-xl border border-[#F0DDE3] shadow-[0_20px_60px_-15px_rgba(222,146,169,0.45)] rounded-2xl p-6 sm:p-7">
        <button
          type="button"
          onClick={() => decide('declined')}
          aria-label="Close and decline optional cookies"
          className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-[#A0978A] hover:text-[#2C2C2C] hover:bg-[#FAF9F7] transition-colors"
          data-testid="cookie-close-btn"
        >
          <X size={16} />
        </button>

        <div className="flex items-start gap-4">
          <span
            className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center"
            style={{ backgroundColor: '#FBF3F5', color: '#DE92A9' }}
          >
            <Cookie size={22} />
          </span>
          <div>
            <p
              className="text-lg leading-snug mb-2"
              style={{ fontFamily: '"Playfair Display", serif', color: '#2C2C2C' }}
            >
              A little something sweet
            </p>
            <p className="text-sm leading-relaxed" style={{ color: '#757B7F' }} data-testid="cookie-banner-text">
              We use essential cookies to keep the site running and, with your permission, optional cookies to
              remember your preferences and understand what our readers love. Read our{' '}
              <Link
                to="/privacy-policy"
                className="underline underline-offset-2 font-medium hover:text-[#C9758F] transition-colors"
                style={{ color: '#DE92A9' }}
                data-testid="cookie-policy-link"
              >
                Privacy & Cookie Policy
              </Link>
              .
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <button
            type="button"
            onClick={() => decide('accepted')}
            className="flex-1 text-white px-6 py-3 text-xs tracking-[0.18em] font-semibold rounded-full transition-all duration-300 hover:tracking-[0.22em] hover:shadow-lg"
            style={{ backgroundColor: '#DE92A9' }}
            data-testid="cookie-accept-btn"
          >
            ACCEPT ALL
          </button>
          <button
            type="button"
            onClick={() => decide('declined')}
            className="flex-1 px-6 py-3 text-xs tracking-[0.18em] font-semibold rounded-full border-2 transition-colors duration-300 hover:bg-[#FBF3F5]"
            style={{ borderColor: '#E8AABC', color: '#2C2C2C' }}
            data-testid="cookie-decline-btn"
          >
            ESSENTIAL ONLY
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
